import { useCallback, useState, type ReactNode } from 'react';

type Val = string | number | null | undefined;

// Click-to-sort table state. Provide accessors keyed by column id; clicking a
// header toggles asc/desc. Non-finite/null values sort last regardless of dir.
export function useTableSort<T>(defaultKey: string | null = null, defaultDir: 'asc' | 'desc' = 'asc') {
  const [key, setKey] = useState<string | null>(defaultKey);
  const [dir, setDir] = useState<'asc' | 'desc'>(defaultDir);

  const onSort = useCallback(
    (k: string) => {
      setKey((cur) => {
        if (cur === k) {
          setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          return cur;
        }
        setDir('asc');
        return k;
      });
    },
    []
  );

  const sortRows = useCallback(
    (rows: T[], accessors: Record<string, (r: T) => Val>): T[] => {
      if (!key || !accessors[key]) return rows;
      const get = accessors[key];
      const isNull = (v: Val) => v == null || (typeof v === 'number' && !isFinite(v));
      return [...rows].sort((a, b) => {
        const av = get(a);
        const bv = get(b);
        const an = isNull(av);
        const bn = isNull(bv);
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
        let c: number;
        if (typeof av === 'number' && typeof bv === 'number') c = av - bv;
        else c = String(av).localeCompare(String(bv));
        return dir === 'asc' ? c : -c;
      });
    },
    [key, dir]
  );

  // Arrow indicator for a header.
  const indicator = useCallback(
    (k: string): ReactNode =>
      key === k ? <span style={{ marginLeft: 3 }}>{dir === 'asc' ? '▲' : '▼'}</span> : null,
    [key, dir]
  );

  return { key, dir, onSort, sortRows, indicator };
}
